import { Form, Formik } from 'formik'
import React, { Suspense, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Enquiry,
  ICurrentUserProfile,
  IEnquiry,
  IEnquiryPreLodgementResponse,
  IEnquiryReferenceItems,
} from '../../../models/Enquiry'
import Conditional from '../../common/Conditional'
import Loader from '../../common/Loader'
import Protected from '../../Protected'
import { IEnquiryStep } from './EnquiryLodgementWizardBreadcrumbs'
import EnquiryService from '../../../services/EnquiryService'
import FormErrorDisplay from '../../common/FormErrorMessages'
import FormSuccessMessages from '../../common/FormSuccessMessages'
import {
  IValidatorRefDataDTOItems,
  TellUsMoreValidator,
} from '../validation-schema/TellUsMoreValidator'
import { EnquiryLodgementWizardSteps } from '../EnquiryHelper'
import { IEnquiryWizardNavigationProps } from './EnquiryLodgementWizardActions'
import LoaderContext, { LoaderProvider } from '../../context/FormLoader'
import useErrorHandler from '../../../hooks/useErrorHandler'
import axios from 'axios'
import { fetchCurrentUserProfile, stringifyErrors } from '../EnquiryCommon'
import EnquiryLodgementWizardStepRenderer from './EnquiryLodgementWizardStepRenderer'
import EnquiryLodgementWizardHeading from './EnquiryLodgementWizardHeading'
import { MainArticleLayout } from '../../../layouts/MainArticleLayout'
import { buildSelectItem } from '../../../utilities/commonFunctions'

const EnquiryLodgementStep = React.lazy(
  () => import('../enquiry-steps/EnquiryLodgementStep')
)

const TellUsMoreProvidedInfo = React.lazy(
  () => import('../enquiry-steps/TellUsMoreProvidedInfo')
)

const EnquiryLodgementWizardActions = React.lazy(
  () => import('./EnquiryLodgementWizardActions')
)

const EnquiryLodgementWizardBreadcrumbs = React.lazy(
  () => import('./EnquiryLodgementWizardBreadcrumbs')
)

const EnquiryLodgementWizard = () => {
  const { id, step } = useParams()
  const mountedRef = useRef(false)
  const loader = useContext(LoaderContext)
  const formRef = useRef(null)
  const [steps, setSteps] = useState<IEnquiryStep[]>()
  const [activeStep, setActiveStep] = useState<number>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [userProfile, setUserProfile] = useState<ICurrentUserProfile>()
  const [pageHeading, setPageHeading] = useState('')
  const [userErrors, addErrors, handleErrors, , clearErrors] = useErrorHandler()
  const [userSuccess, setUserSuccess] = useState(null)
  const [canSave, setCanSave] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isFormSubmitting, setFormSubmitting] = useState(false)
  const [preLodgementData, setPreLodgementData] =
    useState<IEnquiryPreLodgementResponse>()
  const [enquiry, setEnquiry] = useState<IEnquiry>(new Enquiry())
  const isLanding = activeStep === 0
  const isLastStep = activeStep + 1 === EnquiryLodgementWizardSteps.length
  const isSubmitStep = activeStep + 1 === EnquiryLodgementWizardSteps.length - 1
  const increment = () => setActiveStep(activeStep + 1)
  const [validatorOptions, setValidtorOptions] =
    useState<IValidatorRefDataDTOItems>({
      yesNoTypes: [],
      employmentTypes: [],
      reasonNotspokenTypes: [],
    })
  const selectItems: IEnquiryReferenceItems = {
    employmentTypes: [],
    paidLeaveTypes: [],
    apprenticeTypes: [],
    independentContractorTypes: [],
    genuineContractEmployeeTypes: [],
    personDescTypes: [],
    visaTypes: [],
    countryTypes: [],
    officeLocationTypes: [],
    languageTypes: [],

    notSpokenToEmployerTypes: [],
    othersAffectedTypes: [],
    evidenceTypes: [],
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()
    loader.up()

    const currentStep: number = step && parseInt(step) <= 1 ? parseInt(step) : 0 //Querystring check
    const steps = getSteps(0, currentStep, false)

    if (mountedRef.current) {
      setPageHeading(steps[currentStep].heading)
      setSteps(steps)
    }

    EnquiryService.getUpdateEnquiryViewModel(1, id)
      .then((data) => {
        if (mountedRef.current) {
          const result = data.result

          const preLodgement: IEnquiryPreLodgementResponse = {
            id: result.id,
            enquiryNumber: result.number,
            enquiryStatus: result.status,
            isOpen: result.isOpen,
            requestForInfoType: result.requestForInfoType,
            requestForInfoSubTypes: result.requestForInfoSubTypes,
            otherQuestions: result.otherQuestions,
            recommendedLinks: result.recommendedLinks,
          }
          setPreLodgementData(preLodgement)

          setValidtorOptions((prevState) => {
            return {
              ...prevState,
              yesNoTypes:
                result.independentContractorTypes === undefined
                  ? []
                  : result.independentContractorTypes,
              employmentTypes:
                result.employmentTypes === undefined
                  ? []
                  : result.employmentTypes,
            }
          })

          selectItems.employmentTypes = buildSelectItem(
            result.employmentTypes,
            false
          )
          selectItems.paidLeaveTypes = buildSelectItem(
            result.paidLeaveTypes,
            false
          )
          selectItems.apprenticeTypes = buildSelectItem(
            result.apprenticeTypes,
            false
          )
          selectItems.independentContractorTypes = buildSelectItem(
            result.independentContractorTypes,
            false
          )
          selectItems.genuineContractEmployeeTypes = buildSelectItem(
            result.genuineContractEmployeeTypes,
            false
          )
          selectItems.personDescTypes = buildSelectItem(
            result.personDescTypes,
            false
          )
          selectItems.visaTypes = buildSelectItem(result.visaTypes, true)
          selectItems.countryTypes = buildSelectItem(result.countryTypes, true)
          selectItems.officeLocationTypes = buildSelectItem(
            result.officeLocationTypes,
            true
          )
          selectItems.languageTypes = buildSelectItem(
            result.languageTypes,
            true
          )

          //agreement needs to be false everytime it loads.
          const enquiry = {
            ...result,
            enquiryId: result.id,
            agreement: false,
            referenceTypes: selectItems,
          }
          setEnquiry(enquiry)
        }
      })
      .then(() => {
        fetchCurrentUserProfile(source).then((profileData) => {
          if (mountedRef.current) {
            setUserProfile(profileData)
          }
        })
      })
      .then(() => {
        if (mountedRef.current) {
          setActiveStep(currentStep)
        }
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        setIsLoading(false)
        loader.down()
      })

    return () => {
      mountedRef.current = false
    }
  }, [])

  const proceed = async () => {
    //This populates the enquiry fields that are meant to be populated by default.
    if (isLanding && userProfile) {
      if (
        enquiry.businessABN?.length <= 0 &&
        enquiry.businessName?.length <= 0 &&
        enquiry.noOfEmployees?.length <= 0
      ) {
        const enq = {
          ...enquiry,
          businessName: userProfile.businessName,
          businessABN: userProfile.businessABN,
          noOfEmployees: userProfile.noOfEmployees,
        }
        setEnquiry(enq)
      }
    }

    const canMove = await canProgress()
    if (canMove) {
      move()
    }
  }

  const save = async () => {
    if (isLastStep || isSubmitStep) {
      setIsSaving(true)
      const payload = new Enquiry(formRef.current.values)
      await commit(payload, 2) //This submits the previous step. We dont want to raise a submit
      return
    }

    clearErrors()
    if (formRef.current) {
      const errors = await formRef.current.validateForm()
      stringifyErrors(errors, addErrors, enquiry)

      if (Object.keys(errors).length > 0) {
        window.scrollTo(0, 0)
      } else {
        const payload = new Enquiry(formRef.current.values)
        setIsSaving(true)
        let success = false
        await commit(payload, activeStep).then((data) => {
          success = data
        })
        if (success) {
          await getNextEnquiryViewModel(payload)
          move()
          setUserSuccess(
            "We have saved your Enquiry. We'll close your enquiry if you don't submit it within 14 days"
          )
        }
      }
    }
  }

  const canProgress = async () => {
    clearErrors()
    setUserSuccess(null)
    if (formRef.current && !isSubmitStep) {
      const errors = await formRef.current.validateForm()
      stringifyErrors(errors, addErrors, enquiry)

      if (Object.keys(errors).length > 0) {
        window.scrollTo(0, 0)
        return false
      }
    }
    return true
  }

  const next = async () => {
    const canMove = await canProgress()
    if (canMove) {
      await saveCurrentEnquiry()
    }
  }

  const saveCurrentEnquiry = async () => {
    if (formRef.current) {
      const payload = new Enquiry(formRef.current.values)
      setIsSaving(true)
      let success = false
      await commit(payload, activeStep).then((data) => {
        success = data
      })
      if (success) {
        await getNextEnquiryViewModel(payload)
        move()
      }
    }
  }

  const getNextEnquiryViewModel = async (enq: Enquiry) => {
    const next = activeStep + 1

    if (next > 0 && next < 3) {
      setIsLoading(true)
      await EnquiryService.getUpdateEnquiryViewModel(next, id)
        .then((data) => {
          if (mountedRef.current) {
            const result = data.result

            setValidtorOptions((prevState) => {
              return {
                ...prevState,
                reasonNotspokenTypes:
                  result.notSpokenToEmployerTypes === undefined
                    ? []
                    : result.notSpokenToEmployerTypes,
              }
            })

            selectItems.notSpokenToEmployerTypes = buildSelectItem(
              result.notSpokenToEmployerTypes,
              false
            )
            selectItems.othersAffectedTypes = buildSelectItem(
              result.othersAffectedTypes,
              false
            )
            selectItems.evidenceTypes = buildSelectItem(
              result.evidenceTypes,
              false
            )

            const updatedEnq = {
              ...enq,
              ...result,
              referenceTypes: {
                ...enq.referenceTypes,
                notSpokenToEmployerTypes: selectItems.notSpokenToEmployerTypes,
                othersAffectedTypes: selectItems.othersAffectedTypes,
                evidenceTypes: selectItems.evidenceTypes,
              },
            }
            setEnquiry(updatedEnq)
          }
        })
        .catch((error) => {
          handleErrors(error)
          window.scrollTo(0, 0)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const navigate = async (enquiryStep: IEnquiryStep) => {
    const canMove = await canProgress()
    if (!canMove) {
      return
    }

    let showStep
    let tempSteps
    let current
    if (activeStep > enquiryStep.step && activeStep - enquiryStep.step >= 1) {
      tempSteps = steps.slice()
      showStep = enquiryStep.step
      current = enquiryStep.step

      for (let index = activeStep; index > current; index--) {
        tempSteps[index].done = false
        tempSteps[index].current = false
      }
      tempSteps[current].current = true
      tempSteps[current].done = false
    } else if (
      activeStep < enquiryStep.step &&
      enquiryStep.step - activeStep > 1
    ) {
      showStep = enquiryStep.step
      tempSteps = getSteps(enquiryStep.step - 1, showStep, true)
    } else {
      showStep = enquiryStep.step
      tempSteps = getSteps(enquiryStep.step, showStep, true)
    }

    setSteps(tempSteps)
    setPageHeading(tempSteps[showStep].heading)
    setActiveStep(showStep)
    setCanSave(true) //RESET BACK TO SAVE
    window.scrollTo(0, 0)
  }

  const move = () => {
    const current = activeStep
    const next = current + 1
    const tempSteps = getSteps(current, next, true)
    setSteps(tempSteps)
    setPageHeading(tempSteps[next].heading)
    increment()
    setCanSave(true) //RESET BACK TO SAVE
    window.scrollTo(0, 0)
  }

  const reset = () => {
    const current = activeStep
    const next = 1
    const tempSteps = getSteps(current, next, false)

    setSteps(tempSteps)
    setPageHeading(tempSteps[next].heading)
    setActiveStep(next)
    setCanSave(true) //RESET BACK TO SAVE
    window.scrollTo(0, 0)
  }

  const getSteps = (stop: number, current: number, done: boolean) => {
    const tempSteps = EnquiryLodgementWizardSteps.slice()
    for (let index = 0; index <= stop; index++) {
      tempSteps[index].done = done
      tempSteps[index].current = false
    }
    tempSteps[current].current = true
    tempSteps[current].done = false
    return tempSteps
  }

  const handleFormSubmit = async (values: Enquiry) => {
    const payload = new Enquiry(values)
    if (!canSave) {
      setEnquiry(payload)
      move()
      return
    }

    await commit(payload, activeStep)
  }

  const commit = async (payload: Enquiry, step: number) => {
    let success = false
    await EnquiryService.submit(step, payload)
      .then(() => {
        addErrors(null)

        const submitStep = step === steps.length - 2
        if (submitStep) {
          move() //Move to the final step
          setUserSuccess('Thank you for submitting your enquiry.')
          sessionStorage.setItem('Reload', 'true')
        }

        window.scrollTo(0, 0)
        success = true
      })
      .catch((error) => {
        setUserSuccess(null)
        handleErrors(error)
        window.scrollTo(0, 0)
        success = false
      })
      .finally(() => {
        setIsSaving(false)
        setFormSubmitting(false)
      })

    return success
  }

  const enquiryWizardNavigationProps: IEnquiryWizardNavigationProps = {
    next: next,
    save: save,
    enquiryId: id,
    isSubmitStep: isSubmitStep,
    isSubmitting: false,
    isSaving: false,
    enquiryIsOpen: enquiry?.isOpen,
    isValid: false,
    onError: handleErrors,
  }

  return (
    <Protected>
      <Suspense fallback={null}>
        <MainArticleLayout>
          <EnquiryLodgementWizardHeading
            step={activeStep}
            pageHeading={pageHeading}
            preLodgementData={preLodgementData}
          />

          <FormErrorDisplay
            show={userErrors?.length > 0}
            errorsList={userErrors}
            heading="Please review the following errors"
          />
          <FormSuccessMessages content={userSuccess} heading="Success" />

          <Conditional if={isLoading && activeStep <= 0}>
            <Loader />
          </Conditional>

          <Conditional if={activeStep === 0}>
            <EnquiryLodgementStep
              next={proceed}
              data={preLodgementData}
              onError={handleErrors}
              onSuccess={setUserSuccess}
            />
          </Conditional>

          <LoaderProvider>
            <Conditional if={activeStep > 0}>
              <Formik
                innerRef={formRef}
                validationSchema={new TellUsMoreValidator(
                  validatorOptions
                ).getRule(activeStep - 1)}
                enableReinitialize
                initialValues={enquiry}
                validateOnMount={isSubmitStep}
                onSubmit={(values) => {
                  setFormSubmitting(true)
                  handleFormSubmit(values)
                }}
              >
                {({ handleSubmit, isValid }) => (
                  <Form
                    className="ui form osp-variant large"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    <Conditional if={!isLastStep}>
                      <EnquiryLodgementWizardBreadcrumbs
                        steps={steps}
                        navigate={navigate}
                      />
                      <TellUsMoreProvidedInfo data={preLodgementData} />
                    </Conditional>

                    <Conditional if={activeStep > 0}>
                      <EnquiryLodgementWizardStepRenderer
                        step={activeStep}
                        enquiry={enquiry}
                        reset={reset}
                        onError={handleErrors}
                      />
                    </Conditional>

                    <Conditional if={!isLastStep}>
                      <br />
                      <EnquiryLodgementWizardActions
                        {...enquiryWizardNavigationProps}
                        isSubmitting={isFormSubmitting}
                        isSaving={isSaving}
                        isValid={isValid}
                      />
                    </Conditional>
                  </Form>
                )}
              </Formik>
            </Conditional>
          </LoaderProvider>
        </MainArticleLayout>
      </Suspense>
    </Protected>
  )
}

export default EnquiryLodgementWizard
